import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setAutoPlay } from '../redux/slices/musicPlayerSlice';
import { addToNextUp } from '../utils/queueAdd';
import { resolveTrackSrc } from '../utils/resolvePlaybackSrc';
import { likeTrack, unlikeTrack } from '../redux/thunk/likesThunk';
import { setActiveTab } from '../redux/slices/appSlice';
import { setPendingStart } from '../redux/slices/whatsNextSlice';
import { useToast } from '../contexts/ToastContext';
import AddToPlaylistModal from './AddToPlaylistModal';
import TrackMixAppearancesModal from './DJPal/TrackMixAppearancesModal';
import { cancelDelayedClick } from './DJPal/MixPlayer';
import { isWeb, isSamplesOnly } from '../utils/webOnly';
import { entityId } from '../utils/entityId';
import { track as trackEvent } from '../utils/analytics';
import { positionMenuAtPoint, positionMenuByTrigger } from '../utils/menuPosition';
import { useDesignV2 } from '../utils/designV2';
import './TrackContextMenu.scss';

// Module-level singleton: at most one TrackContextMenu dropdown can be open
// at a time. The previously-open instance's closer is invoked synchronously
// when a new one opens. More reliable than relying on document-level
// outside-click detection, which can be racy across React's synthetic event
// system.
let _activeCloser = null;

// `mixAppearances` opt-in prop:
//   { mode: 'byArtist', subjectArtist: { name, id } } — show
//     "View in <subject>'s mixes" (track played in mixes by that DJ).
//   { mode: 'all' } — show "Show all DJs who played this" (all mixes
//     containing the track, no DJ filter).
//   undefined — action is hidden.
// `onAddToQueue(track)` / `onAddToPlaylist(track)` opt-in overrides: when
// provided, they replace the built-in flows. Needed for local-file tabs
// (Manage/Prepare) where the internal resolveTrackSrc path returns an
// unplayable raw filesystem path and AddToPlaylistModal targets an
// `online://` reference instead of the real file. `onSearch(event, track)`
// renders a "Search in Mixpedia" item when set and the track has a title.
// `desktopLike`: show the Like item on desktop too (not just mobile). As of
// the 2026-07 consistency pass EVERY track-row surface passes it (local tabs,
// Mixpedia pages, Crate Digger) — Like is reachable from the menu everywhere,
// and the hover LikeButton on rows deliberately duplicates it (Spotify-style).
// Leave it off only for a surface whose artist/title identity is too weak to
// like (filename-guessed rows).
// `hideLike`: suppress the Like item entirely — for the page-header ⋯, where
// the prominent LikeButton is always visible right next to it (row menus keep
// it: on mobile rows the menu is the only Like path).
// `analyticsFrom`: surface tag for the menu analytics events
// (e.g. 'playlist_sidebar'); undefined surfaces report without it.
// `openAtPoint` ({ x, y, nonce } | null): opens THIS menu as an OS-style
// right-click context menu anchored at the given viewport point instead of the
// ⋯ button. The host row's onContextMenu bumps `nonce` on every right-click so
// repeat clicks at the same spot re-open it. Keeping right-click and kebab on
// the same component is what guarantees the two menus never drift apart.
// `onLocateFile(track)`: opt-in "Locate file…" item for rows whose file is
// missing on disk. `onRestoreFromBackup(track)`: opt-in "Restore from Cloud
// Backup" item for missing rows the caller knows are in the user's backup.
// `fileMissing`: disables the items that need the file to
// exist (playback/queue/playlist/reveal) while leaving Copy/Search usable.
const TrackContextMenu = ({ track, onSearch, onAddToQueue, onAddToPlaylist, mixAppearances, onRemoveFromLibrary, removeLabel = 'Remove from Library', desktopLike = false, hideLike = false, analyticsFrom, openAtPoint = null, onLocateFile, onRestoreFromBackup, fileMissing = false }) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const designV2 = useDesignV2();
    // Cross-menu contract (see MixContextMenu): opening any menu broadcasts
    // its id; every other open menu closes. The pointerdown stopPropagation
    // guards swallow sibling menus' outside-click closers otherwise.
    const menuIdRef = useRef(`track-${Math.random().toString(36).slice(2)}`);
    const [copied, setCopied] = useState(false);
    const [showPlaylistModal, setShowPlaylistModal] = useState(false);
    const [showMixAppearancesModal, setShowMixAppearancesModal] = useState(false);
    const menuRef = useRef(null);
    const btnRef = useRef(null);
    const dispatch = useDispatch();
    const toast = useToast();
    const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
    const likedSet = useSelector(state => state.likes.likedSet);
    const likeKey = track.artist && track.title ? `${track.artist}|||${track.title}` : null;
    const isLiked = likeKey ? likedSet[likeKey] : false;

    const getArtwork = useCallback(() => {
        return track.artwork || track.artworkUrl || track.image || '';
    }, [track]);

    const getPreviewSrc = useCallback(() => {
        return track.previewUrl || track.sampleUrl || track.preview_url || track.preview || '';
    }, [track]);

    // Set when the menu was opened via right-click (openAtPoint) — positions
    // the dropdown at the cursor instead of the ⋯ button. Cleared on close and
    // on button-opens so a later kebab click doesn't reuse a stale point.
    const [pointAnchor, setPointAnchor] = useState(null);

    const closeMenu = useCallback(() => {
        setMenuOpen(false);
        setPointAnchor(null);
        if (_activeCloser === closeMenu) _activeCloser = null;
    }, []);

    const handleToggleMenu = (e) => {
        e.stopPropagation();
        e.preventDefault();
        setPointAnchor(null);
        setMenuOpen(prev => {
            if (!prev) window.dispatchEvent(new CustomEvent('djpal-context-menu-open', { detail: menuIdRef.current }));
            const next = !prev;
            if (next) {
                // Close any other open menu before opening this one.
                if (_activeCloser && _activeCloser !== closeMenu) _activeCloser();
                _activeCloser = closeMenu;
                trackEvent('track_context_menu_opened', { trigger: 'kebab', ...(analyticsFrom ? { from: analyticsFrom } : {}) });
            } else if (_activeCloser === closeMenu) {
                _activeCloser = null;
            }
            return next;
        });
    };

    // Right-click open: the host row bumps openAtPoint.nonce per contextmenu
    // event; each bump opens this menu at that point (same singleton +
    // cross-menu-close contract as the button path).
    const lastPointNonceRef = useRef(null);
    useEffect(() => {
        if (!openAtPoint || openAtPoint.nonce === lastPointNonceRef.current) return;
        lastPointNonceRef.current = openAtPoint.nonce;
        window.dispatchEvent(new CustomEvent('djpal-context-menu-open', { detail: menuIdRef.current }));
        if (_activeCloser && _activeCloser !== closeMenu) _activeCloser();
        _activeCloser = closeMenu;
        setPointAnchor({ x: openAtPoint.x, y: openAtPoint.y });
        setMenuOpen(true);
        trackEvent('track_context_menu_opened', { trigger: 'right_click', ...(analyticsFrom ? { from: analyticsFrom } : {}) });
    }, [openAtPoint, closeMenu, analyticsFrom]);

    useEffect(() => {
        const onOtherMenu = (e) => { if (e.detail !== menuIdRef.current) setMenuOpen(false); };
        window.addEventListener('djpal-context-menu-open', onOtherMenu);
        return () => window.removeEventListener('djpal-context-menu-open', onOtherMenu);
    }, []);

    // Release the singleton slot if this instance unmounts while open.
    useEffect(() => () => {
        if (_activeCloser === closeMenu) _activeCloser = null;
    }, [closeMenu]);

    const handleAddToNextUp = async (e) => {
        e.stopPropagation();
        setMenuOpen(false);

        if (onAddToQueue) {
            onAddToQueue(track);
            return;
        }

        // Adding to the queue must never START playback: when the queue is
        // empty and nothing is playing, a lingering autoPlay=true from an
        // earlier session makes jinke play the first added track immediately.
        dispatch((d, getState) => {
            const mp = getState().musicPlayer;
            const audio = window.musicPlayerInstance;
            const playingNow = !!(audio && audio.src && !audio.paused);
            if ((mp.audioList?.length || 0) === 0 && !playingNow && mp.autoPlay) d(setAutoPlay(false));
        });
        // Queue instantly. Playback policy (full-first): a track with a known
        // YT/SC link queues a /lazy-audio URL — the audio server extracts the
        // FULL stream only when the player actually requests it, redirecting
        // to the sample on extract failure and 404ing (→ skip) when there's
        // no fallback. Sample-only tracks queue the sample directly.
        const snippet = getPreviewSrc();
        const ytId = [track.youtubeId, track.youtube_id].find(v => v && v !== '-1') || null;
        const scUrl = track.soundcloudUrl || track.soundcloud_url || null;
        const base = {
            name: track.title,
            singer: track.artist,
            cover: getArtwork(),
            ...(ytId ? { youtubeId: ytId } : {}),
            ...(scUrl ? { soundcloudUrl: scUrl } : {}),
            ...(snippet ? { samplePreviewUrl: snippet } : {}),
        };
        const trackId = entityId(track);
        const canSearch = trackId && track.artist && track.title;
        if ((ytId || scUrl || canSearch) && track.playbackBlocked !== true && !isSamplesOnly) {
            // Known link → extract at play time. No link but identifiable →
            // /lazy-audio runs the same search+score the play button uses,
            // also at play time. Sample rides along as the fallback.
            const q = ytId ? `yt=${encodeURIComponent(ytId)}`
                : scUrl ? `sc=${encodeURIComponent(scUrl)}`
                : `tid=${encodeURIComponent(trackId)}&artist=${encodeURIComponent(track.artist)}&title=${encodeURIComponent(track.title)}`
                    + (track.duration ? `&dur=${encodeURIComponent(track.duration)}` : '')
                    + (() => { try { const t = localStorage.getItem('djpal:token'); return t ? `&tok=${encodeURIComponent(t)}` : ''; } catch { return ''; } })();
            const fb = snippet ? `&fallback=${encodeURIComponent(snippet)}` : '';
            addToNextUp(dispatch, {
                ...base,
                musicSrc: `http://localhost:3069/lazy-audio?${q}${fb}`,
                ...(ytId || scUrl ? { originalUrl: ytId ? `https://www.youtube.com/watch?v=${ytId}` : scUrl } : {}),
            });
            // Prefetch the full download NOW (fire-and-forget, deduped): by
            // the time the queue reaches this track, /lazy-audio serves the
            // local file — instant start + waveform ready, same as direct
            // plays.
            if (ytId || scUrl) {
                const pf = ytId ? `https://www.youtube.com/watch?v=${ytId}` : scUrl;
                try { window.electron?.djpal?.getTrackAudio?.(pf, { priority: false })?.catch?.(() => {}); } catch (_) { /* best-effort */ }
            }
            return;
        }
        if (snippet) {
            addToNextUp(dispatch, { ...base, musicSrc: snippet });
            return;
        }
        // No sample and no known link: needs a lazy YouTube search first —
        // the one genuinely slow case, so show progress and never fail silent.
        toast.info(`Adding "${track.title}" to Next Up…`);
        const resolved = await Promise.race([
            resolveTrackSrc(track),
            new Promise((r) => setTimeout(() => r(null), 45000)),
        ]);
        if (!resolved) {
            toast.warning(`Couldn't load audio for "${track.title}"`);
            return;
        }
        addToNextUp(dispatch, {
            ...base,
            musicSrc: resolved.musicSrc,
            ...(resolved.isHls ? { isHls: true, hlsSrc: resolved.hlsSrc } : {}),
            ...(resolved.originalUrl ? { originalUrl: resolved.originalUrl } : {}),
        });
    };

    const handleCopyTrackName = (e) => {
        e.stopPropagation();
        const text = `${track.artist} - ${track.title}`;
        navigator.clipboard.writeText(text).then(() => {
            // Inline confirmation: swap the item to "Copied ✓" briefly, then close.
            setCopied(true);
            setTimeout(() => { setCopied(false); setMenuOpen(false); }, 900);
        }).catch(() => {
            toast.error('Failed to copy');
            setMenuOpen(false);
        });
    };

    const handleAddToPlaylist = (e) => {
        e.stopPropagation();
        setMenuOpen(false);
        if (onAddToPlaylist) {
            onAddToPlaylist(track);
            return;
        }
        setShowPlaylistModal(true);
    };

    const handleSearch = (e) => {
        e.stopPropagation();
        setMenuOpen(false);
        if (onSearch) onSearch(e, track);
    };

    const handleRemoveFromLibrary = (e) => {
        e.stopPropagation();
        setMenuOpen(false);
        if (onRemoveFromLibrary) onRemoveFromLibrary(track);
    };

    const handleStartSet = (e) => {
        e.stopPropagation();
        setMenuOpen(false);
        const title = (track.title || '').trim();
        if (!title) return;
        const artist = (track.artist || '').trim();
        const query = artist ? `${artist} - ${title}` : title;
        dispatch(setPendingStart({
            query,
            meta: {
                title,
                artist,
                artwork: getArtwork() || null,
                previewUrl: getPreviewSrc() || null,
                // Carry local-file identity + any analyzed metadata so the
                // planner can fall back to library-based recommendations when
                // the track isn't in Mixpedia.
                filePath: track.filePath || null,
                bpm: track.bpm ?? null,
                key: track.key ?? null,
                genre: track.genre ?? null,
            },
        }));
        dispatch(setActiveTab('whats-next'));
    };

    const handleShowInFolder = (e) => {
        e.stopPropagation();
        setMenuOpen(false);
        if (track.filePath) {
            window.electron.shell.showItemInFolder(track.filePath);
            trackEvent('track_show_in_folder', analyticsFrom ? { from: analyticsFrom } : {});
        }
    };

    // Close menu on outside click or Escape. (The "close other menus" logic
    // lives in handleToggleMenu via the _activeCloser singleton — more
    // deterministic than relying on the document mousedown listener firing
    // for clicks on another instance's button, which is racy with React's
    // synthetic stopPropagation.)
    useEffect(() => {
        if (!menuOpen) return undefined;

        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target) &&
                btnRef.current && !btnRef.current.contains(e.target)) {
                closeMenu();
            }
        };
        const handleEsc = (e) => {
            if (e.key === 'Escape') closeMenu();
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEsc);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEsc);
        };
    }, [menuOpen, closeMenu]);

    // Position dropdown near the button, rendered via portal to avoid overflow
    // clipping. useLayoutEffect runs synchronously after DOM mutation but
    // before paint, so the dropdown never flashes at the off-screen default
    // (top:100% inside body) before the fixed coords are applied.
    const [dropdownStyle, setDropdownStyle] = useState(null);

    // Right-click positioning: the menu renders hidden at the raw click point
    // first, gets measured, then positionMenuAtPoint flips/clamps it on-screen
    // before paint (the documented utils/menuPosition pattern).
    useLayoutEffect(() => {
        if (!menuOpen || !pointAnchor || !menuRef.current) return;
        const { left, top } = positionMenuAtPoint(menuRef.current, pointAnchor.x, pointAnchor.y);
        setDropdownStyle({ position: 'fixed', left, top, right: 'auto', bottom: 'auto', zIndex: 10001 });
    }, [menuOpen, pointAnchor]);

    // Kebab positioning: same measure-then-place pattern as the point path.
    // The menu renders hidden for one layout pass, then its REAL size drives
    // the flip. (This used to guess a fixed 210px height — fine for the 5-item
    // Mixpedia menu, wrong for the ~8-item Manage/Prepare menu, which then
    // "had room" and opened downward straight off the bottom of the viewport.)
    useLayoutEffect(() => {
        if (!menuOpen || !btnRef.current || pointAnchor || !menuRef.current) {
            if (!menuOpen) setDropdownStyle(null);
            return;
        }
        const rect = btnRef.current.getBoundingClientRect();
        const { width } = menuRef.current.getBoundingClientRect();
        // Open downward by default; only flip up when the menu would actually
        // clip off the bottom of the viewport. (The menu portals at z-index
        // 10001, so it renders OVER the bottom player bar — no need to reserve
        // it, which previously made far too many rows open upward.)
        // Anchor to the button's right edge (dropdown extends left) unless the
        // button sits too close to the left edge to fit it — then anchor left
        // (extends right) so the menu never clips off-screen.
        // The SCSS `margin-top: 4px` shifts the box down after placement — it
        // acts as the gap when opening downward, so cancel it when opening up.
        // Let the helper do the horizontal work too: align the menu's right
        // edge to the ⋯ button (or left edges when the button sits too far
        // left to fit) and clamp inside the viewport. The previous manual
        // `right: innerWidth - rect.right` math skipped the clamp and could
        // land the menu detached from (or off) the trigger on device
        // geometries where innerWidth and layout coords disagree.
        const placed = positionMenuByTrigger(menuRef.current, rect, {
            align: rect.right >= width ? 'right' : 'left',
        });
        const top = placed.openUp ? placed.top - 4 : placed.top;
        setDropdownStyle({
            position: 'fixed',
            // Explicitly clear `right`: the .track-context-dropdown SCSS
            // defaults to `top: 100%; right: 0`, and a stale `right` together
            // with `left` stretches the menu to the viewport edge. That was
            // the "⋯ opens nothing" on lower rows.
            left: placed.left,
            right: 'auto',
            top,
            bottom: 'auto',
            // Above any host popover (the feed mixes dropdown is z-index 9999);
            // a context menu must render on top of the surface that opened it.
            zIndex: 10001,
        });
    }, [menuOpen, pointAnchor]);

    // Cancel any pending row delayedClick BEFORE React's onClick on the
    // button gets a chance to flip menu state. Without this, a stray
    // earlier tap on the row body queues a 250ms navigation that fires
    // right after the menu opens and unmounts it — looking exactly like
    // "the ⋯ tap didn't work".
    const handleWrapperPointerDown = (e) => {
        cancelDelayedClick();
        e.stopPropagation();
    };

    return (
        <div
            className="track-more-wrapper"
            onPointerDown={handleWrapperPointerDown}
            onMouseDown={e => e.stopPropagation()}
            onTouchStart={e => { cancelDelayedClick(); }}
            onClick={e => e.stopPropagation()}
        >
            <button
                ref={btnRef}
                className="track-more-btn"
                title="More options"
                onClick={handleToggleMenu}
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="5" cy="12" r="2.5" />
                    <circle cx="12" cy="12" r="2.5" />
                    <circle cx="19" cy="12" r="2.5" />
                </svg>
            </button>

            {menuOpen && ReactDOM.createPortal(
                <div
                    className="track-context-dropdown"
                    ref={menuRef}
                    // Both open paths render hidden for one layout pass so the
                    // effects above can measure the real menu before placing it.
                    // right/bottom MUST be cleared: the .track-context-dropdown
                    // SCSS defaults to `right: 0`, and left+right together
                    // stretch the menu to the viewport edge — the flip logic
                    // then measures that bogus width and pins the menu to the
                    // left margin.
                    style={dropdownStyle || { position: 'fixed', left: pointAnchor?.x ?? 0, top: pointAnchor?.y ?? 0, right: 'auto', bottom: 'auto', visibility: 'hidden', zIndex: 10001 }}
                    onClick={e => e.stopPropagation()}
                    onMouseDown={e => e.stopPropagation()}
                >
                    {/* "Search in Mixpedia" is opt-in via the onSearch prop
                        (rendered below, after Copy). On Mixpedia surfaces the row
                        tap already navigates there, so those callers omit it;
                        the Manage/Prepare tabs pass onSearch to surface it. */}
                    {onLocateFile && (
                        <div className="dropdown-item" onClick={(e) => { e.stopPropagation(); setMenuOpen(false); setPointAnchor(null); onLocateFile(track); }}>
                            <span className="dropdown-icon">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="11" cy="11" r="8" />
                                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                </svg>
                            </span>
                            Locate file…
                        </div>
                    )}
                    {/* Opt-in like onLocateFile: rows whose file is missing on
                        this machine but present in Cloud Backup. */}
                    {onRestoreFromBackup && (
                        <div className="dropdown-item" onClick={(e) => { e.stopPropagation(); setMenuOpen(false); setPointAnchor(null); onRestoreFromBackup(track); }}>
                            <span className="dropdown-icon">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M20 16.58A5 5 0 0 0 18 7h-1.26A8 8 0 1 0 4 15.25" />
                                    <polyline points="8 17 12 21 16 17" />
                                    <line x1="12" y1="12" x2="12" y2="21" />
                                </svg>
                            </span>
                            Restore from Cloud Backup
                        </div>
                    )}
                    {!hideLike && isAuthenticated && likeKey && (
                        <div className={`dropdown-item${desktopLike ? '' : ' mobile-only-item'}`} onClick={(e) => { e.stopPropagation(); setMenuOpen(false); if (isLiked) dispatch(unlikeTrack({ artist: track.artist, title: track.title })); else dispatch(likeTrack(track)); }}>
                            <span className="dropdown-icon">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill={isLiked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                                </svg>
                            </span>
                            {designV2 ? (isLiked ? 'Unlike' : 'Like') : (isLiked ? 'Unlike Track' : 'Like Track')}
                        </div>
                    )}
                    <div className={`dropdown-item${fileMissing ? ' disabled' : ''}`} onClick={fileMissing ? undefined : handleAddToPlaylist}>
                        <span className="dropdown-icon">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="3" y1="5" x2="21" y2="5" />
                                <line x1="3" y1="11" x2="21" y2="11" />
                                <line x1="3" y1="17" x2="12" y2="17" />
                                <line x1="18" y1="14" x2="18" y2="22" />
                                <line x1="14" y1="18" x2="22" y2="18" />
                            </svg>
                        </span>
                        Add to playlist
                    </div>
                    <div className={`dropdown-item${fileMissing ? ' disabled' : ''}`} onClick={fileMissing ? undefined : handleAddToNextUp}>
                        <span className="dropdown-icon">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="3" width="18" height="18" rx="2" />
                                <line x1="12" y1="8" x2="12" y2="16" />
                                <line x1="8" y1="12" x2="16" y2="12" />
                            </svg>
                        </span>
                        Add to next up
                    </div>
                    <div className="dropdown-item" onClick={handleCopyTrackName}>
                        <span className="dropdown-icon">
                            {copied ? (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                            ) : (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                                </svg>
                            )}
                        </span>
                        {copied ? 'Copied' : 'Copy track name'}
                    </div>
                    {onSearch && track.title && (
                        <div className="dropdown-item" onClick={handleSearch}>
                            <span className="dropdown-icon">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="11" cy="11" r="7" />
                                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                </svg>
                            </span>
                            Search in Mixpedia
                        </div>
                    )}
                    {mixAppearances && (
                        <div
                            className="dropdown-item"
                            onClick={(e) => {
                                e.stopPropagation();
                                setMenuOpen(false);
                                setShowMixAppearancesModal(true);
                            }}
                        >
                            <span className="dropdown-icon">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="9" />
                                    <path d="M12 7v5l3 2" />
                                </svg>
                            </span>
                            {mixAppearances.mode === 'byArtist' && mixAppearances.subjectArtist?.name
                                ? `View in ${mixAppearances.subjectArtist.name}'s mixes`
                                : 'Show DJs who played this'}
                        </div>
                    )}
                    {track.title && (
                        <div className={`dropdown-item${fileMissing ? ' disabled' : ''}`} onClick={fileMissing ? undefined : handleStartSet}>
                            <span className="dropdown-icon">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="5" cy="12" r="2.5" />
                                    <path d="M7.5 12 H20" />
                                    <path d="M16 8 l4 4 -4 4" />
                                </svg>
                            </span>
                            Start a set with this track
                        </div>
                    )}
                    {/* Local-file surfaces only: gated on the track carrying a
                        real filesystem path (Mixpedia tracks have none; cloud
                        library rows carry online:// or http(s):// pseudo-paths)
                        and on real Electron desktop (the web/Capacitor shims
                        stub shell.showItemInFolder as a no-op). */}
                    {!isWeb && track.filePath && !/^(online|https?):\/\//.test(track.filePath) && (
                        <div className={`dropdown-item${fileMissing ? ' disabled' : ''}`} onClick={fileMissing ? undefined : handleShowInFolder}>
                            <span className="dropdown-icon">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                                </svg>
                            </span>
                            {/^Mac/.test(navigator.platform) ? 'Show in Finder' : 'Show in Explorer'}
                        </div>
                    )}
                    {onRemoveFromLibrary && (
                        <>
                            <div className="dropdown-divider" />
                            <div className="dropdown-item danger" onClick={handleRemoveFromLibrary}>
                                <span className="dropdown-icon">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="3 6 5 6 21 6" />
                                        <path d="M19 6l-2 14H7L5 6" />
                                        <path d="M10 11v6M14 11v6" />
                                        <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
                                    </svg>
                                </span>
                                {removeLabel}
                            </div>
                        </>
                    )}
                </div>,
                document.body
            )}

            {showPlaylistModal && ReactDOM.createPortal(
                <AddToPlaylistModal
                    show={showPlaylistModal}
                    onHide={() => setShowPlaylistModal(false)}
                    track={track}
                />,
                document.body
            )}

            {showMixAppearancesModal && (
                <TrackMixAppearancesModal
                    track={track}
                    subjectArtist={mixAppearances?.mode === 'byArtist' ? mixAppearances.subjectArtist : null}
                    onClose={() => setShowMixAppearancesModal(false)}
                />
            )}
        </div>
    );
};

export default TrackContextMenu;
